import Vec2 from 'vec2';

import { EditableLabel } from '../EditableLabel';

import {
  LABEL_OFFSET,
  OFFSET_MEASUREMENT,
  OFFSET_MEASUREMENT_TEXT,
} from '~/core/constants';
import { LineDirection } from '~/core/enums';
import { getDirection } from '~/core/helpers';
import { ArrowInfo, DrawingShape, LineSegment } from '~/core/models';
import { enforceEdgeConstraints } from '~/core/utils';
import theme from '~/theme/theme';

const {
  palette,
  drawingTool: {
    drawingShape: { stroke },
  },
} = theme.light;

export class EditableLengthLabel extends EditableLabel {
  isInterior = false;

  constructor(
    parentShape: DrawingShape,
    lineSegment: LineSegment,
    rootArrowInfo: ArrowInfo,
    label: string,
    onEdit: (
      editedArrow: ArrowInfo,
      shape: DrawingShape,
      enteredValue?: number
    ) => void,
    canvas: fabric.Canvas,
    interiorColor: string = palette.black,
    activeDrawingShape?: DrawingShape
  ) {
    super(parentShape, canvas, interiorColor, label, rootArrowInfo);
    if (rootArrowInfo.size === undefined) {
      console.error(
        'Tried to init editableLabel with undefined length',
        rootArrowInfo
      );
    }

    if (rootArrowInfo.isInterior) {
      this.measurementText.stroke = interiorColor;
      this.measurementText.fill = interiorColor;
      this.isInterior = true;
    }

    this.measurementText.on('editing:exited', () => {
      if (this.measurementText.text) {
        const edge = parentShape.drawingEdges.find(
          (edge) => edge.id === rootArrowInfo.drawingEdgeId
        );
        const arrowSize = parseFloat(
          this.measurementText.text.replace(',', '.')
        );
        const size = enforceEdgeConstraints({
          arrow: rootArrowInfo,
          value: arrowSize,
          edgeType: edge?.sideItem.type,
          shape: parentShape,
        });

        onEdit({ ...rootArrowInfo, size }, parentShape, arrowSize);
      } else {
        // handling the case where the user enters an empty string
        // or is otherwise undefined.
        onEdit({ ...rootArrowInfo }, parentShape);
      }
    });

    const isParentShapeNameSameAsActiveDrawing =
      parentShape.canvasObjectName === activeDrawingShape?.canvasObjectName;

    const pos = this.getMeasurementPosition(
      lineSegment,
      isParentShapeNameSameAsActiveDrawing
    );

    this.measurementText.left = pos.x;
    this.measurementText.top = pos.y;
    this.labelText.left = pos.x - LABEL_OFFSET;
    this.labelText.top = pos.y;
    canvas.add(this.measurementText);
    if (isParentShapeNameSameAsActiveDrawing) {
      canvas.add(this.labelText);
    }
  }

  lighten(): void {
    if (this.isInterior) {
      return;
    }

    this.measurementText.set({
      stroke: stroke.normal,
      fill: stroke.normal,
    });

    this.labelText.set({
      stroke: stroke.normal,
      fill: stroke.normal,
    });
  }

  darken(): void {
    if (this.isInterior) {
      return;
    }

    this.labelText.set({
      stroke: stroke.active,
      fill: stroke.active,
    });

    this.measurementText.set({
      stroke: stroke.active,
      fill: stroke.active,
    });
  }

  getMeasurementPosition(
    arrow: LineSegment,
    isParentShapeNameSameAsActiveDrawing: boolean
  ): Vec2 {
    const diffVec = arrow.endPoint.subtract(arrow.startPoint, true);
    const normalVec = diffVec.rotate(Math.PI / 2, undefined, true).normalize();
    const pos = arrow.startPoint.add(arrow.endPoint, true).multiply(0.5);
    const boundRc = this.measurementText.getBoundingRect();
    const direction = getDirection(arrow);

    let sign = 1;

    switch (direction) {
      case LineDirection.TOP:
        pos.add(
          normalVec.multiply(OFFSET_MEASUREMENT_TEXT + boundRc.height, true)
        );
        pos.set(pos.x - boundRc.width * 0.5, pos.y, false);
        break;
      case LineDirection.BOTTOM:
        pos.add(normalVec.multiply(OFFSET_MEASUREMENT_TEXT, true));
        pos.set(pos.x - boundRc.width * 0.5, pos.y, false);
        break;
      case LineDirection.VERTICAL_LEFT:
        pos.set(
          pos.x - boundRc.width - OFFSET_MEASUREMENT,
          pos.y - boundRc.height * 0.5,
          false
        );
        break;
      case LineDirection.VERTICAL_RIGHT:
        pos.set(
          pos.x +
            OFFSET_MEASUREMENT +
            (isParentShapeNameSameAsActiveDrawing ? LABEL_OFFSET : 0),
          pos.y - boundRc.height * 0.5,
          false
        );
        break;
      case LineDirection.LEFT:
        sign = normalVec.y < 0 ? -1 : 0;
        pos.set(pos.x - boundRc.width, pos.y + sign * boundRc.height, false);
        break;
      default:
        sign = normalVec.y < 0 ? -1 : 0;
        pos.set(pos.x, pos.y + sign * boundRc.height, false);
        break;
    }

    //Pos refinement
    if (
      sign === 0 &&
      (direction === LineDirection.LEFT || direction === LineDirection.RIGHT)
    ) {
      pos.set(pos.x, pos.y + OFFSET_MEASUREMENT, false);
    }

    return pos;
  }
}
